/**
 * Invoices Service
 *
 * Type-safe invoice management service using @monorepo/shared schemas
 */

import {
  type CreateInvoice,
  CreateInvoiceSchema,
  type InvoicePreview,
  type InvoicePreviewResponse,
  InvoicePreviewResponseSchema,
  InvoicePreviewSchema,
  type InvoiceQuery,
  type InvoiceResponse,
  InvoiceResponseSchema,
  type PaginatedInvoicesResponse,
  PaginatedInvoicesResponseSchema,
  type UpdateInvoice,
  UpdateInvoiceSchema,
  type VoidInvoice,
  VoidInvoiceSchema,
} from '@monorepo/shared/schemas/invoices'
import { z } from 'zod'
import { get, post, put } from '@/lib/api-client'

// Success response schema for actions like finalize/void
const SuccessResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  id: z.string().optional(),
  status: z.string().optional(),
  invoice_number: z.string().optional(),
  finalized_at: z.string().optional(),
  voided_at: z.string().optional(),
  void_reason: z.string().optional(),
})

export type SuccessResponse = z.infer<typeof SuccessResponseSchema>

export const invoicesService = {
  /**
   * List all invoices (filtered by query)
   */
  async getInvoices(query?: InvoiceQuery): Promise<PaginatedInvoicesResponse> {
    const queryParams = new URLSearchParams()

    if (query?.search) {
      queryParams.append('search', query.search)
    }
    if (query?.customer_id) {
      queryParams.append('customer_id', query.customer_id)
    }
    if (query?.contract_id) {
      queryParams.append('contract_id', query.contract_id)
    }
    if (query?.status) {
      queryParams.append('status', query.status)
    }
    if (query?.payment_status) {
      queryParams.append('payment_status', query.payment_status)
    }
    if (query?.currency) {
      queryParams.append('currency', query.currency)
    }
    if (query?.issue_date_from) {
      queryParams.append('issue_date_from', query.issue_date_from)
    }
    if (query?.issue_date_to) {
      queryParams.append('issue_date_to', query.issue_date_to)
    }
    if (query?.sort) {
      queryParams.append('sort', query.sort)
    }
    if (query?.order) {
      queryParams.append('order', query.order)
    }
    if (query?.filter_field) {
      queryParams.append('filter_field', query.filter_field)
    }
    if (query?.filter_operator) {
      queryParams.append('filter_operator', query.filter_operator)
    }
    if (query?.filter_value) {
      queryParams.append('filter_value', query.filter_value)
    }
    if (query?.page) {
      queryParams.append('page', query.page.toString())
    }
    if (query?.limit) {
      queryParams.append('limit', query.limit.toString())
    }

    const url = `/invoices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedInvoicesResponseSchema)
  },

  /**
   * Get invoice by ID (includes line items and taxes)
   */
  async getInvoice(id: string): Promise<InvoiceResponse> {
    return get(`/invoices/${id}`, InvoiceResponseSchema)
  },

  /**
   * Create new invoice
   */
  async createInvoice(data: CreateInvoice): Promise<InvoiceResponse> {
    const validated = CreateInvoiceSchema.parse(data)

    return post('/invoices', validated, InvoiceResponseSchema)
  },

  /**
   * Update invoice (only draft invoices can be updated)
   */
  async updateInvoice(
    id: string,
    data: UpdateInvoice
  ): Promise<InvoiceResponse> {
    const validated = UpdateInvoiceSchema.parse(data)

    return put(`/invoices/${id}`, validated, InvoiceResponseSchema)
  },

  /**
   * Finalize invoice (makes it official and ready to send)
   */
  async finalizeInvoice(id: string): Promise<SuccessResponse> {
    return post(`/invoices/${id}/finalize`, {}, SuccessResponseSchema)
  },

  /**
   * Void invoice (marks as canceled)
   */
  async voidInvoice(id: string, data: VoidInvoice): Promise<SuccessResponse> {
    const validated = VoidInvoiceSchema.parse(data)

    return post(`/invoices/${id}/void`, validated, SuccessResponseSchema)
  },

  /**
   * Preview invoice (calculate what an invoice would look like)
   */
  async previewInvoice(data: InvoicePreview): Promise<InvoicePreviewResponse> {
    const validated = InvoicePreviewSchema.parse(data)

    return post('/invoices/preview', validated, InvoicePreviewResponseSchema)
  },
}

// Helper to convert cents to currency display
export function formatCentsAsCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(cents / 100)
}

// Helper to format date string
export function formatInvoiceDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}
